import React, { useEffect, useMemo, useRef, useState } from "react";

import { Mode, ScreenProps, SettingsOnChangeProps } from "./AnimationTypes";
import AnimationElement from "./AnimationElement";
import AnimationSettings from "./AnimationSettings";
import useAnimation from "./useAnimation";
import styles from "./animation.module.scss";
import { ANIMATIONS } from "../../utils/constants";

type AnimationListProps = ScreenProps & { mode: Mode };
const MARGIN_RIGHT_GAP = 12;

const AnimationList = ({
  mode,
  context,
  preview,
  activeElement
}: AnimationListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [itemsPerRow, setItemsPerRow] = useState(0);

  useEffect(() => {
    const calculateItemsPerRow = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const itemWidth = containerRef.current.children[0].clientWidth;
        const itemsInRow = Math.floor(containerWidth / (itemWidth + MARGIN_RIGHT_GAP));
        setItemsPerRow(itemsInRow);
      }
    };

    calculateItemsPerRow();
    window.addEventListener("resize", calculateItemsPerRow);

    return () => {
      window.removeEventListener("resize", calculateItemsPerRow);
    };
  }, []);

  const {
    getAnimations,
    getAnimationTime,
    getSelectedAnimation,
    handleSelectAnimation
  } = useAnimation(mode, context, preview, activeElement);
  const time = getAnimationTime();
  const selectedAnimation = getSelectedAnimation(time) || {};

  const memoizedAnimations = useMemo(
    () => getAnimations().map(({ label, value: type }, index) => {
      let currentType = selectedAnimation.type;
      switch (currentType) {
        case ANIMATIONS.SCALE:
          if (selectedAnimation.easing === "elastic-in-out") currentType = ANIMATIONS.POP;
          break;
        case ANIMATIONS.SLIDE:
          if (selectedAnimation.distance) currentType = ANIMATIONS.FLOAT;
          break;
        default:
          break;
      }
      const remainder = (index + 1) % itemsPerRow;
      const division = (index + 1) / itemsPerRow;
      const rowOrder = remainder === 0 ? division - 1 : Math.floor(division);
      const isSelected = type === (currentType || ANIMATIONS.NONE);
      const showSettings = isSelected && type !== ANIMATIONS.NONE;
      const selectAnimationProps = { time, type };

      const onElementClick = () => handleSelectAnimation(selectAnimationProps);
      const onSettingsChange = ({
        subtype,
        duration,
        color
      }: SettingsOnChangeProps) => handleSelectAnimation({
        ...selectAnimationProps,
        subtype,
        duration,
        color
      });

      return (
        <>
          <AnimationElement
            type={type}
            label={label}
            isSelected={isSelected}
            order={rowOrder * 2}
            onClick={onElementClick}
          />
          {showSettings && (
            <AnimationSettings
              type={type}
              selectedAnimation={selectedAnimation}
              order={rowOrder * 2 + 1}
              onChange={onSettingsChange}
            />
          )}
        </>
      );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [
      mode,
      time,
      selectedAnimation,
      activeElement.source.animations,
      itemsPerRow
    ]);

  return (
    <div ref={containerRef} className={styles.List}>
      {memoizedAnimations}
    </div>
  );
};

export default AnimationList;
